import * as THREE from 'three';
import { Spring, Spring3, damp } from '../engine/spring';
import type { Fx } from '../fx/Fx';
import type { Glows } from '../fx/Glows';
import type { Cell } from '../scene/World';
import { KIND_COLOR, KIND_ELEMENT, MAX_LEVEL, type Kind } from './config';
import { ORB_RADIUS, RelicPool, type Pool } from './RelicPool';

/* ---------- shared geometry ---------- */

/**
 * Pedestal: unit height (0..1), scaled per relic to reach the cup — a flared foot, a ringed neck and a
 * widening throat, so the orb stands on a small altar of its own rather than a stick.
 */
export const stemGeo = new THREE.LatheGeometry([
  [0, 0], [0.11, 0], [0.115, 0.04], [0.08, 0.1], [0.05, 0.18], [0.042, 0.3], [0.05, 0.36], [0.04, 0.42],
  [0.036, 0.62], [0.05, 0.78], [0.075, 0.92], [0.085, 1], [0, 1],
].map(([x, y]) => new THREE.Vector2(x, y)), 14);
/** Cup the orb hovers over: a shallow bowl with a lip, origin at its rim. */
let clawGeo: THREE.BufferGeometry | null = null;
export function clawGeometry(): THREE.BufferGeometry {
  if (clawGeo) return clawGeo;
  const bowl = new THREE.LatheGeometry([
    [0.0, -0.05], [0.06, -0.05], [0.12, -0.03], [0.155, 0.0], [0.165, 0.012], [0.15, 0.016], [0.13, -0.004], [0.06, -0.03], [0.0, -0.032],
  ].map(([x, y]) => new THREE.Vector2(x, y)), 24);
  clawGeo = bowl.toNonIndexed();
  clawGeo.computeVertexNormals();
  return clawGeo;
}

/* ---------- relic ---------- */

/** `hold`: dropped onto a partner, waiting for the simulation to accept the merge. */
export type RelicState = 'idle' | 'drag' | 'hold' | 'return' | 'absorb' | 'gone';

export interface RelicDeps { scene: THREE.Scene; glows: Glows; floorGlows: Glows; fx: Fx; camera: THREE.Camera; pool: RelicPool }

const _v = new THREE.Vector3();
const _c = new THREE.Color();
const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _s = new THREE.Vector3(), _e = new THREE.Euler();
const PEDESTAL = new THREE.Color(0xa07a52);
const RANK_GOLD = new THREE.Color(0xffc86a).multiplyScalar(2.2);
const RING_TINT = {
  r: new THREE.Color(0xb26939), b: new THREE.Color(0x6c94a7), y: new THREE.Color(0xc29b45),
  g: new THREE.Color(0x58866c), a: new THREE.Color(0x8675ad), w: new THREE.Color(0xa8a5b4),
  v: new THREE.Color(0x975058), t: new THREE.Color(0xb9985b), o: new THREE.Color(0x685c7b),
  k: new THREE.Color(0x3a302c), s: new THREE.Color(0x5d8c94),
};
const BRONZE = new THREE.Color(0x8a5530).multiplyScalar(1.4), IRON = new THREE.Color(0x26221f), GOLD = new THREE.Color(0xc8964e).multiplyScalar(1.6);

/** What each grade adds on top of the stone itself. */
const GRADE = {
  size: [0.9, 1.0, 1.1, 1.2, 1.32],
  glow: [0.8, 0.95, 1.08, 1.2, 1.32],
  halo: [0.75, 0.95, 1.2, 1.5, 1.9],
  satellites: [0, 1, 2, 3, 5],
  rune: [0, 0, 0.45, 0.8, 1.3],
  /** Armillary hoops around the orb: a single band, then crossing rings, then a full armillary. */
  rings: [1, 2, 2, 3, 3],
  spin: [0.3, 0.38, 0.5, 0.65, 0.85],
};

/**
 * A relic: a glass orb hovering over a bronze altar. Pure view +
 * animation; the Board decides what happens. Everything moves through
 * springs. Geometry lives in shared instanced pools — the Object3Ds here are
 * never added to the scene, they only compose matrices.
 */
export class Relic {
  readonly root = new THREE.Group();
  private readonly gemPivot = new THREE.Group();
  private readonly claw = new THREE.Object3D();
  private readonly stem = new THREE.Object3D();
  private readonly gem = new THREE.Object3D();
  private gemSlot: { pool: Pool; i: number };
  private stemSlot: number; private clawSlot: number;
  private sats: number[] = [];
  private bands: number[] = [];
  private rings: number[] = [];
  private rune = -1;
  readonly color = new THREE.Color();
  colorHex: number;

  state: RelicState = 'idle';
  readonly pos: Spring3;
  readonly lift = new Spring(0, 0, 260, 22);
  readonly scale = new Spring(0, 1, 320, 14);
  readonly rise = new Spring(-0.35, 0, 200, 16);
  /** Extra spin velocity (kicked on upgrade, decays). */
  readonly spin = new Spring(0, 0, 4, 2.2);
  /** Rune / satellite reveal (0..1) — animates in after an upgrade. */
  readonly aura = new Spring(1, 1, 40, 9);
  /** Drag selection / merge resonance, smoothed independently of hit flashes. */
  readonly focus = new Spring(0, 0, 90, 18);
  private spinAngle = Math.random() * 6.28;
  private punch = 0;
  private attackAge = -1; private attackDuration = 1; private charge = 0;
  private channelT = 0;
  private attackX = 0; private attackZ = -1;
  private poseLift = 0; private poseReach = 0; private poseLean = 0; private pop = 0;
  /** Seconds until the next idle flourish (a slow celebratory turn). */
  private flourishT = 8 + Math.random() * 8;
  private flash = 0;
  private tiltX = 0; private tiltZ = 0;
  private time = Math.random() * 10;
  private readonly ph = Math.random() * 6.28;
  private readonly seed = Math.random();
  private readonly prev = new THREE.Vector3();
  private moteT = 0; private beamT = 0; private flareT = 0;
  private swell = 0;

  private halo: number; private glint: number; private pool: number; private spill: number;
  readonly gemHeight: number;
  readonly s: number;
  readonly dim: boolean;

  constructor(private readonly d: RelicDeps, public cell: Cell, public kind: Kind, public level: number, o: { scale?: number; dim?: boolean } = {}) {
    // a relic fills its well: the co-op well (1.18 m) is the reference, solo's larger wells get larger orbs
    this.s = o.scale ?? Math.max(1, cell.w / 1.18);
    this.dim = !!o.dim;
    this.colorHex = KIND_COLOR[kind];
    this.color.set(this.colorHex);
    this.gemHeight = cell.topY - cell.floorY + 0.3 * this.s;
    const S = this.s, P = d.pool;
    this.root.add(this.stem, this.claw, this.gemPivot);
    this.gemPivot.add(this.gem);
    this.stemSlot = P.stems.alloc(); this.clawSlot = P.claws.alloc();
    const metal = this.dim ? IRON : BRONZE;
    P.stems.color(this.stemSlot, metal); P.claws.color(this.clawSlot, metal);
    const gp = P.gem(kind, level);
    this.gemSlot = { pool: gp, i: gp.alloc() };
    this.pos = new Spring3(cell.x, cell.floorY, cell.z, 220, 24);
    this.root.position.set(cell.x, cell.floorY, cell.z);
    this.prev.copy(this.root.position);
    this.syncGradeExtras();

    this.halo = d.glows.add(this.colorHex, cell.x, cell.topY + this.gemHeight, cell.z, { size: (o.dim ? 0.6 : 0.95) * S, opacity: o.dim ? 0.25 : 0.55 });
    this.glint = d.glows.add(0xffffff, cell.x, cell.topY + this.gemHeight, cell.z, { size: 0.18 * S, opacity: o.dim ? 0.2 : 0.8 });
    this.pool = d.floorGlows.add(this.colorHex, cell.x, cell.floorY + 0.008, cell.z, { size: cell.w * 1.05, opacity: o.dim ? 0.3 : 0.55 });
    this.spill = d.floorGlows.add(this.colorHex, cell.x, cell.topY + 0.004, cell.z, { size: cell.w * 1.9, opacity: o.dim ? 0 : 0.18 });
  }

  private get g() { return Math.min(MAX_LEVEL, Math.max(1, this.level)) - 1; }

  /** Allocate / free satellites and rune to match the grade. */
  private syncGradeExtras() {
    const P = this.d.pool, want = this.dim ? 0 : GRADE.satellites[this.g];
    while (this.sats.length < want) this.sats.push(P.shards.alloc());
    while (this.sats.length > want) P.shards.release(this.sats.pop()!);
    const wantRings = this.dim ? 1 : GRADE.rings[this.g];
    while (this.rings.length < wantRings) this.rings.push(P.rings.alloc());
    while (this.rings.length > wantRings) P.rings.release(this.rings.pop()!);
    while (this.bands.length < this.level) this.bands.push(P.bands.alloc());
    while (this.bands.length > this.level) P.bands.release(this.bands.pop()!);
    const needRune = !this.dim && GRADE.rune[this.g] > 0;
    if (needRune && this.rune < 0) this.rune = P.runes.alloc();
    if (!needRune && this.rune >= 0) { P.runes.release(this.rune); this.rune = -1; }
  }

  /** Change kind / grade in place (merge result). */
  transform(kind: Kind, level: number) {
    this.cancelAttack(); this.charge = 0;
    const P = this.d.pool;
    this.gemSlot.pool.release(this.gemSlot.i);
    const gp = P.gem(kind, level);
    this.gemSlot = { pool: gp, i: gp.alloc() };
    this.kind = kind; this.level = level;
    this.colorHex = KIND_COLOR[kind];
    this.color.set(this.colorHex);
    this.syncGradeExtras();
    this.aura.snap(0); this.aura.target = 1;
    this.d.glows.setColor(this.halo, this.colorHex, 0.55);
    this.d.floorGlows.setColor(this.pool, this.colorHex, 0.55);
    this.d.floorGlows.setColor(this.spill, this.colorHex, 0.18);
  }

  gemPosition(out: THREE.Vector3) { return out.setFromMatrixPosition(this.gem.matrixWorld); }
  get restY() { return this.cell.floorY; }

  prepareAttack(aim: THREE.Vector3, duration: number) {
    this.attackAge = 0; this.attackDuration = duration;
    const dx = aim.x - this.root.position.x, dz = aim.z - this.root.position.z;
    const length = Math.hypot(dx, dz) || 1;
    this.attackX = dx / length; this.attackZ = dz / length;
  }
  cancelAttack() { this.attackAge = -1; this.channelT = 0; }
  fire() {
    this.attackAge = -1; this.punch = 1;
    this.channelT = this.kind === 'r' ? 0.46 : this.kind === 'v' ? 0.32 : this.kind === 't' ? 0.55 : 0;
    // the release is a flare inside the orb, not a jolt
    this.flash = Math.min(1.6, this.flash + (this.kind === 'v' ? 0.45 : this.kind === 't' ? 0.35 : this.kind === 'y' ? 0.6 : 0.75));
  }
  pulse(amount = 1.5) { this.flash = Math.min(5, this.flash + amount); }

  appear() { this.scale.snap(0); this.scale.target = 1; this.scale.kick(8); this.rise.snap(-0.35); this.rise.target = 0; this.flash = 3; this.spin.kick(10); }

  update(dt: number, _camera: THREE.Camera) {
    this.time += dt;
    const t = this.time, S = this.s, g = this.g;
    if (this.state === 'idle' || this.state === 'return') this.pos.set(this.cell.x, this.cell.floorY, this.cell.z);
    this.pos.update(dt); this.lift.update(dt); this.scale.update(dt); this.rise.update(dt); this.spin.update(dt); this.aura.update(dt); this.focus.update(dt);
    const x = this.pos.x.x, y = this.pos.y.x + this.lift.x, z = this.pos.z.x;
    this.root.position.set(x, y + this.rise.x, z);
    if (this.state === 'return' && Math.abs(this.pos.x.x - this.cell.x) < 0.01 && Math.abs(this.pos.z.x - this.cell.z) < 0.01 && this.lift.x < 0.01) this.state = 'idle';

    const vx = (x - this.prev.x) / Math.max(dt, 1e-4), vz = (z - this.prev.z) / Math.max(dt, 1e-4);
    this.prev.set(x, y, z);
    this.tiltZ += (THREE.MathUtils.clamp(-vx * 0.08, -0.5, 0.5) - this.tiltZ) * damp(14, dt);
    this.tiltX += (THREE.MathUtils.clamp(vz * 0.08, -0.5, 0.5) - this.tiltX) * damp(14, dt);
    this.root.rotation.set(this.tiltX, 0, this.tiltZ);

    this.punch = Math.max(0, this.punch - dt * 6);
    this.channelT = Math.max(0, this.channelT - dt);
    this.flash = Math.max(0, this.flash - dt * 3.5);
    if (this.attackAge >= 0) this.attackAge += dt;
    const chargeTarget = this.attackAge < 0 ? 0 : Math.min(1, this.attackAge / this.attackDuration);
    this.charge += (chargeTarget - this.charge) * damp(24, dt);
    const charge = this.charge;
    const vis = this.state === 'gone' ? 0 : 1;
    const s0 = Math.max(0, this.scale.x) * vis;
    const focus = Math.max(0, this.focus.x);
    this.pop += (this.punch - this.pop) * damp(20, dt);
    const cast = Math.max(charge, this.channelT > 0 ? 0.8 : 0);
    this.swell += (cast - this.swell) * damp(cast > this.swell ? 6 : 3.5, dt);
    const sc = s0 * S * GRADE.size[g] * (1 + this.swell * 0.08 + this.pop * 0.04 + focus * 0.035);
    const bottom = ORB_RADIUS * S * GRADE.size[g];
    // No springy attack poses: the orb casts inside itself. It kindles and swells a little while charging,
    // flares on release and eases back, all through smooth exponentials (no overshoot, no bob or lean).
    // A rare idle flourish only brightens the orb.
    if (this.state === 'idle' && this.attackAge < 0 && this.channelT <= 0 && !this.dim && (this.flourishT -= dt) <= 0) {
      this.flourishT = 8 + Math.random() * 8;
      this.pulse(0.35);
    }
    const lift = 0, reach = 0, lean = 0;
    const k = damp(14, dt);
    this.poseLift += (lift - this.poseLift) * k; this.poseReach += (reach - this.poseReach) * k; this.poseLean += (lean - this.poseLean) * k;
    const attackLift = this.poseLift, attackReach = this.poseReach, attackLean = this.poseLean;
    const bob = Math.sin(t * 0.6 + this.ph) * 0.006 * (1 + g * 0.4) + attackLift * S;
    // higher grades float a little higher above the claw
    const float = g * 0.025 * S;
    const gemY = this.gemHeight + bob + float + focus * 0.035 * S;
    // the orb hovers just clear of its cup, never touching it
    const cupY = Math.max(0.02, this.gemHeight - bottom - 0.05 * S);
    this.claw.position.y = cupY;
    this.claw.scale.setScalar(S * (0.95 + g * 0.06) * s0);
    this.stem.scale.set(S * s0, cupY * s0, S * s0);

    this.spinAngle += dt * (GRADE.spin[g] + this.spin.x);
    this.gemPivot.position.set(this.attackX * attackReach * S, gemY, this.attackZ * attackReach * S);
    this.gemPivot.rotation.set((this.kind === 't' ? 0.08 : 0.32) + this.attackZ * attackLean, 0, -this.attackX * attackLean);
    // The sundial keeps a stable face; the garnet slowly rolls around its beating heart.
    this.gem.rotation.set(0, this.kind === 't' ? this.seed * Math.PI * 2 : this.kind === 'v' ? this.spinAngle * 0.35 : this.spinAngle, 0);
    this.gem.scale.setScalar(sc);
    this.root.updateMatrixWorld(true);

    const P = this.d.pool;
    P.stems.set(this.stemSlot, this.stem.matrixWorld);
    P.claws.set(this.clawSlot, this.claw.matrixWorld);
    P.claws.color(this.clawSlot, this.dim ? IRON : g >= 3 ? GOLD : BRONZE);
    P.stems.color(this.stemSlot, this.dim ? IRON : PEDESTAL);
    // Rank: one gold stud per grade set into the cup's lip, facing the camera side first.
    const studs = this.bands.length, cupR = 0.15 * S * (0.95 + g * 0.06) * s0;
    for (let k = 0; k < studs; k++) {
      const a = Math.PI / 2 + (k - (studs - 1) / 2) * 0.62;
      _m.compose(_v.set(Math.cos(a) * cupR, cupY + 0.006 * S, Math.sin(a) * cupR), _q.identity(), _s.setScalar(S * s0)).premultiply(this.root.matrixWorld);
      P.bands.set(this.bands[k], _m);
      P.bands.color(this.bands[k], this.dim ? IRON : RANK_GOLD);
    }

    const hz = 0.22 + this.level * 0.12, p = 0.5 + 0.5 * Math.sin(t * hz * Math.PI * 2 + this.ph);
    const lift01 = Math.min(1, this.lift.x / 0.6);
    const glow = (this.dim ? 0.45 : 1) * GRADE.glow[g] * (0.8 + 0.35 * p) + lift01 * 0.4 + this.swell * 0.7;
    const { pool: gp, i: gi } = this.gemSlot;
    gp.set(gi, this.gem.matrixWorld);
    const element = KIND_ELEMENT[this.kind];
    RelicPool.shade(gp, gi, this.color, glow, this.level, this.flash * 0.9, this.seed, element, focus);

    // satellites: shards orbiting on a tilted ring
    this.gemPosition(_v);
    const gx = _v.x, gz = _v.z, gy = _v.y;
    const aura = Math.max(0, this.aura.x) * s0;
    const n = this.sats.length;
    for (let k = 0; k < n; k++) {
      const a = t * (1.1 + g * 0.15) + (k / n) * Math.PI * 2;
      const r = 0.36 * S * GRADE.size[g] * (0.6 + 0.4 * aura) * (1 - charge * 0.18);
      _v.set(gx + Math.cos(a) * r, gy + Math.sin(a * 2 + this.ph) * 0.05 + Math.sin(a) * 0.08, gz + Math.sin(a) * r * 0.8);
      _q.setFromEuler(_e.set(0.3, a * 2.5, 0.2 * Math.sin(a)));
      const ss = aura * (0.8 + 0.2 * Math.sin(t * 3 + k));
      P.shards.set(this.sats[k], _m.compose(_v, _q, _s.setScalar(ss)));
      RelicPool.shade(P.shards, this.sats[k], this.color, glow, this.level, this.flash * 0.4, this.seed + k, element, focus);
      if (!this.dim && Math.random() < 1 - Math.exp(-dt * 4)) this.d.fx.trail(_v, this.colorHex, 0.04, 1.3, 0.25);
    }
    // Element-specific frames share one torus pool; rank adds hoops without masking the core.
    const orbR = ORB_RADIUS * S * GRADE.size[g] * (1 + this.pop * 0.06), metalTone = this.dim ? IRON : RING_TINT[this.kind];
    for (let k = 0; k < this.rings.length; k++) {
      const big = g >= 4 && k === this.rings.length - 1 ? 1.12 : 1;
      const heartBeat = this.kind === 'v' ? Math.pow(Math.max(0, Math.sin(t * 4 + this.ph)), 12) * 0.025 + charge * 0.035 : 0;
      const r = orbR * (1.2 + k * 0.12 - heartBeat) * big * s0;
      let rr = r, sy = 1, ringY = gy;
      if (this.kind === 'r') { _e.set(Math.PI / 2 - 0.16 + k * 0.1, t * 0.15 + k, 0.06, 'YXZ'); ringY -= orbR * (0.28 - k * 0.1); }
      else if (this.kind === 'b') { _e.set(0.15, this.ph + k * 1.05 + Math.sin(t * 0.2) * 0.1, 0.08, 'YXZ'); sy = 1.17; }
      else if (this.kind === 'y') { _e.set(Math.PI / 2 - 0.42, this.ph, t * 0.16 + k * 0.45, 'YXZ'); rr *= 1 + k * 0.04; }
      else if (this.kind === 'g') { _e.set(Math.PI / 2 - 0.06, this.ph + t * 0.08, k * 0.08, 'YXZ'); ringY -= orbR * (0.38 - k * 0.22); }
      else if (this.kind === 'a') { _e.set(Math.PI / 2 - 0.6, t * 0.35 + this.ph, k * 0.65, 'YXZ'); }
      else if (this.kind === 'v') { _e.set(0, k * Math.PI / 2 + t * 0.25 + this.ph, 0, 'YXZ'); sy = 1.1; }
      else if (this.kind === 'w') _e.set(Math.PI / 2 - 0.55, k * Math.PI / 3 + t * 0.15, 0, 'YXZ');
      else if (this.kind === 'k') { _e.set(Math.PI / 2, k * Math.PI / 4, 0, 'YXZ'); rr *= 1 + k * 0.1; }
      else if (this.kind === 's') _e.set(Math.PI / 2 - 0.1 + Math.sin(t * 2 + k) * 0.06, t * (1.1 + k * 0.5) * (k % 2 ? -1 : 1), 0.1, 'YXZ');
      else if (this.kind === 't') _e.set(Math.PI / 2 + k * 0.22, this.ph + k * 1.1, t * 0.06 * (k % 2 ? -1 : 1), 'YXZ');
      else { _e.set(Math.PI / 2 + Math.sin(t * 0.7 + k) * 0.08, t * (0.6 + k * 0.3), 0.12, 'YXZ'); rr = orbR * (1.45 + k * 0.22) * big * s0; }
      _q.setFromEuler(_e);
      P.rings.set(this.rings[k], _m.compose(_v.set(gx, ringY, gz), _q, _s.set(rr, rr * sy, rr)));
      P.rings.color(this.rings[k], metalTone);
      const rg = (this.dim ? 0.15 : 0.3 + g * 0.14) * (0.8 + 0.25 * p) + this.flash * 0.4 + charge * 0.4;
      P.rings.attrs.iRune.setXYZW(this.rings[k], this.color.r * rg, this.color.g * rg, this.color.b * rg, this.seed * 10 + k);
      P.rings.markShading(this.rings[k]);
    }
    // rune circle on the rim
    if (this.rune >= 0) {
      const away = Math.min(1, Math.hypot(x - this.cell.x, z - this.cell.z) * 2 + lift01);
      // a quiet seal in the element's colour under the orb: present, but no longer competing with its hoops
      const I = GRADE.rune[g] * 0.42 * aura * (1 - away) * (0.75 + 0.2 * p + this.flash * 0.5);
      P.runes.set(this.rune, this.cell.x, this.cell.topY + 0.008, this.cell.z, t * (0.12 + g * 0.04) * (this.seed > 0.5 ? 1 : -1),
        _c.copy(this.color), I, this.cell.d * (0.86 + 0.1 * aura) * (1 + (g - 2) * 0.03));
    }

    // halo, glint, light pools
    this.d.glows.setPos(this.halo, gx, gy, gz);
    this.d.glows.setOpacity(this.halo, s0 * ((this.dim ? 0.12 : 0.18 + g * 0.045) * (0.7 + 0.4 * p) + this.flash * 0.15 + lift01 * 0.15 + focus * 0.1 + charge * 0.1));
    this.d.glows.setSize(this.halo, (this.dim ? 0.6 : 0.95) * S * GRADE.halo[g] * (1 + this.punch * 0.5 + lift01 * 0.3));
    this.d.glows.setPos(this.glint, gx - 0.05 * S, gy + 0.08 * S, gz + 0.06);
    this.d.glows.setOpacity(this.glint, s0 * ((this.dim ? 0.08 : 0.15 + g * 0.02) + this.flash * 0.12));
    const away = Math.min(1, Math.hypot(x - this.cell.x, z - this.cell.z) * 2 + lift01 * 0.6);
    const poolBase = this.dim ? 0.3 : 0.45 + g * 0.08;
    this.d.floorGlows.setOpacity(this.pool, s0 * (1 - away * 0.85) * (poolBase * (0.65 + 0.55 * p) + this.flash * 0.4));
    this.d.floorGlows.setPos(this.pool, this.cell.x, this.cell.floorY + 0.008, this.cell.z);
    this.d.floorGlows.setOpacity(this.spill, s0 * (this.dim ? 0 : (0.12 + g * 0.06 + this.flash * 0.15) * (1 - away * 0.85)));
    this.d.floorGlows.setPos(this.spill, this.cell.x, this.cell.topY + 0.004, this.cell.z);

    if (this.dim || !vis) return;
    _v.set(gx, gy, gz);
    // idle motes: higher grades shed more light
    if (g >= 1 && (this.moteT -= dt) <= 0) {
      this.moteT = 1.4 / (g + 1);
      this.d.fx.embers(_v, this.colorHex, 1, { radius: 0.2 * S, rise: 0.35 + g * 0.1, life: 1.3, size: 0.035 + g * 0.008, intensity: 1.4 + g * 0.3 });
    }
    // star glints on the facets from grade 3
    if (g >= 2 && (this.flareT -= dt) <= 0) {
      this.flareT = 2.4 + Math.random() * 1.6;
      this.d.fx.flare(_s.set(gx + (Math.random() - 0.5) * 0.2 * S, gy + (Math.random() - 0.2) * 0.15 * S, gz + 0.05), this.colorHex, 0.18 + g * 0.025, 0.24, 1.2);
    }
    // legendary: a standing shaft of light
    if (g >= 4 && (this.beamT -= dt) <= 0) {
      this.beamT = 4.0 + this.seed * 2;
      this.d.fx.beam(_s.set(x, this.cell.floorY, z), this.colorHex, 0.15, 1.5, 0.55, 0.55);
    }
  }

  dispose() {
    this.state = 'gone'; this.cancelAttack();
    const P = this.d.pool;
    P.stems.release(this.stemSlot); P.claws.release(this.clawSlot); this.gemSlot.pool.release(this.gemSlot.i);
    for (const s of this.sats) P.shards.release(s);
    for (const b of this.bands) P.bands.release(b);
    for (const r of this.rings) P.rings.release(r);
    if (this.rune >= 0) P.runes.release(this.rune);
    this.d.glows.remove(this.halo); this.d.glows.remove(this.glint);
    this.d.floorGlows.remove(this.pool); this.d.floorGlows.remove(this.spill);
  }
}
